import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type ButtonVariant =
  | 'primary' // the affirmative action: build, launch, confirm
  | 'secondary' // neutral action on a panel
  | 'ghost' // low-emphasis, sits inside dense rows
  | 'danger' // destructive or hostile: attack, abandon, delete
  | 'premium'; // spends Chronos crystals — always magenta, always distinct

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Glyph before the label. */
  icon?: ReactNode;
  /** Glyph after the label — use for directional actions. */
  iconRight?: ReactNode;
  /** Shows a spinner and blocks interaction while a server action is in flight. */
  loading?: boolean;
  /** Stretch to the container width — the default inside cards and modals. */
  fullWidth?: boolean;
}

/**
 * The HUD's action control.
 *
 * `premium` exists as its own variant on purpose: the GDD requires paid
 * shortcuts to be unmistakable, so anything spending Chronos crystals is
 * magenta and never reuses the primary treatment.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'border-silicate/45 bg-silicate/12 text-silicate-bright',
    'hover:border-silicate/80 hover:bg-silicate/20 hover:shadow-glow',
    'active:bg-silicate/28',
  ),
  secondary: cn(
    'border-edge bg-surface-3 text-content-secondary',
    'hover:border-edge-strong hover:bg-surface-raised hover:text-content',
    'active:bg-surface-2',
  ),
  // Interactive text sits at `content-secondary`, never `content-muted` — a
  // control the player is meant to click has to clear WCAG AA on its own.
  ghost: cn(
    'border-transparent bg-transparent text-content-secondary',
    'hover:bg-surface-3 hover:text-content',
    'active:bg-surface-2',
  ),
  danger: cn(
    'border-danger/45 bg-danger/12 text-danger-bright',
    'hover:border-danger/80 hover:bg-danger/20 hover:shadow-glow-danger',
    'active:bg-danger/28',
  ),
  premium: cn(
    'border-chronos/50 bg-chronos/12 text-chronos-bright',
    'hover:border-chronos/85 hover:bg-chronos/22 hover:shadow-glow-chronos',
    'active:bg-chronos/30',
  ),
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1.5 px-2.5 text-label',
  md: 'h-9 gap-2 px-3.5 text-xs',
  lg: 'h-11 gap-2.5 px-5 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    iconRight,
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center rounded-control border',
        'font-display font-medium uppercase tracking-[0.1em]',
        'transition-all duration-[var(--sg-duration-fast)] ease-hud',
        'disabled:pointer-events-none disabled:opacity-45 disabled:saturate-50',
        SIZES[size],
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="h-3.5 w-3.5 animate-[orbit_0.8s_linear_infinite] rounded-full border-[1.5px] border-current border-t-transparent"
        />
      ) : (
        icon
      )}
      {children ? <span className="truncate">{children}</span> : null}
      {!loading && iconRight ? iconRight : null}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'iconRight' | 'fullWidth'> {
  /** Required — an icon-only control must still name itself for screen readers. */
  label: string;
}

/** Square icon-only variant, used in panel headers and dense table rows. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', className, ...rest },
  ref,
) {
  const box = size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
  return (
    <Button
      ref={ref}
      size={size}
      aria-label={label}
      title={label}
      className={cn('!px-0', box, className)}
      {...rest}
    />
  );
});
